/**
 * Lesson side panel for the course player: Overview · Notes · Community · Results.
 *
 *   <LessonPanel ref={panelRef} api={api} courseId={…} node={selectedNode} playerRef={playerRef}
 *                user={user} theme="light|dark" readOnly={isPreview} />
 *   panelRef.current.takeNote()   // pause the video, capture its time and open the note composer
 *
 * Overview  – the tutor's overview of the lesson (node.overview, HTML).
 * Notes     – the learner's private notes; a note taken while a video plays carries the video time,
 *             and clicking that time jumps the video there.
 * Community – questions / posts from anyone in the course, with replies; tutors are badged.
 * Results   – what the teacher scored this lesson at, newest first, with the earlier marks
 *             underneath. Opened by the result chip beside the lesson title.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import RichHtml from '../richtext/RichText';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { formatTime } from './VideoPlayer';
import { NodeResultsTab } from './NodeResults';
import { Info, NotePencil, ChatsCircle, Target, CaretDoubleRight, Play, Trash, PencilSimple, Check, X, Question, Megaphone, ChalkboardTeacher, CheckCircle, ArrowBendUpLeft, Clock } from '@phosphor-icons/react';

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
};

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';
const isTutorRole = (role) => role === 'instructor' || role === 'tenant_admin' || role === 'super_admin';

/** Clickable "▶ 3:21" chip that seeks the video. */
function TimeChip({ seconds, onSeek, dark, testid }) {
  if (seconds === null || seconds === undefined) return null;
  return (
    <button type="button" onClick={() => onSeek?.(seconds)} title="Jump to this point in the video" data-testid={testid}
      className={`inline-flex items-center gap-1 rounded px-1.5 h-5 text-[11px] font-mono font-medium ${dark ? 'bg-sky-500/20 text-sky-300 hover:bg-sky-500/30' : 'bg-sky-50 text-sky-700 hover:bg-sky-100'}`}>
      <Play className="w-3 h-3" weight="fill" />{formatTime(seconds)}
    </button>
  );
}

// ---------------------------------------------------------------- Overview

function OverviewTab({ node, dark, user, courseId }) {
  const staff = isTutorRole(user?.role) && !user?._preview;
  if (!node.overview) {
    return (
      <div className={`p-6 text-center text-[13px] ${dark ? 'text-white/50' : 'text-muted-foreground'}`} data-testid="overview-empty">
        <Info className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>The tutor hasn't written an overview for this lesson yet.</p>
        {staff && <Link to={`/instructor/courses/${courseId}/nodes/${node.id}`} className="text-shell-primary underline mt-2 inline-block">Add an overview</Link>}
      </div>
    );
  }
  return <RichHtml html={node.overview} className={`p-4 text-[14px] leading-relaxed ${dark ? 'text-white/90' : ''}`} data-testid="overview-content" />;
}

// ---------------------------------------------------------------- Notes

function NoteComposer({ initial, timestamp, onTimestampClear, onSave, onCancel, saving, dark, isVideo }) {
  const [text, setText] = useState(initial || '');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = (e) => { e?.preventDefault?.(); if (!text.trim()) return; onSave(text.trim()); };
  return (
    <form onSubmit={submit} className={`rounded-md border p-2 space-y-2 ${dark ? 'border-white/15 bg-white/5' : 'border-border bg-card'}`} data-testid="note-composer">
      {isVideo && (
        <div className="flex items-center gap-2 text-[11px]">
          {timestamp !== null && timestamp !== undefined ? (
            <>
              <span className={`inline-flex items-center gap-1 rounded px-1.5 h-5 font-mono ${dark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-50 text-sky-700'}`}><Clock className="w-3 h-3" />at {formatTime(timestamp)}</span>
              <button type="button" className={`underline ${dark ? 'text-white/50' : 'text-muted-foreground'}`} onClick={onTimestampClear}>remove time</button>
            </>
          ) : <span className={dark ? 'text-white/50' : 'text-muted-foreground'}>No video time attached</span>}
        </div>
      )}
      <Textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Write your note…" className={`text-[13px] ${dark ? 'bg-black/30 border-white/15 text-white' : ''}`}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(e); if (e.key === 'Escape') onCancel(); }} data-testid="note-text" />
      <div className="flex justify-end gap-1.5">
        <Button type="button" variant="ghost" size="sm" className="h-7" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" className="h-7" disabled={saving || !text.trim()} data-testid="note-save"><Check className="w-3.5 h-3.5" /> Save note</Button>
      </div>
    </form>
  );
}

const NotesTab = forwardRef(function NotesTab({ api, courseId, node, playerRef, isVideo, dark, readOnly, onSeek, onCount }, ref) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(null); // { timestamp } | null
  const [editing, setEditing] = useState(null);   // note id
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/courses/${courseId}/nodes/${node.id}/notes`); setNotes(r.data || []); onCount?.((r.data || []).length); }
    catch { setNotes([]); }
    finally { setLoading(false); }
  }, [api, courseId, node.id, onCount]);
  useEffect(() => { load(); }, [load]);

  const takeNote = useCallback(async () => {
    let timestamp = null;
    const p = playerRef?.current;
    if (isVideo && p?.canSeek) {
      try { timestamp = Math.floor(await p.getCurrentTime()); p.pause(); } catch { timestamp = null; }
    }
    setEditing(null);
    setComposer({ timestamp });
  }, [playerRef, isVideo]);
  useImperativeHandle(ref, () => ({ takeNote }), [takeNote]);

  const save = async (text) => {
    if (readOnly) { toast.info('Preview only — notes are not saved'); setComposer(null); return; }
    setSaving(true);
    try {
      await api.post(`/courses/${courseId}/nodes/${node.id}/notes`, { text, timestamp_seconds: composer?.timestamp ?? null });
      setComposer(null); await load(); toast.success('Note saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the note'); }
    finally { setSaving(false); }
  };
  const update = async (id, text) => {
    setSaving(true);
    try { await api.put(`/notes/${id}`, { text }); setEditing(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update the note'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/notes/${id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete the note'); }
  };

  const muted = dark ? 'text-white/50' : 'text-muted-foreground';
  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between gap-2 px-3 py-2 border-b ${dark ? 'border-white/10' : 'border-border'}`}>
        <span className={`text-[12px] ${muted}`}>{notes.length === 0 ? 'Your private notes for this lesson' : `${notes.length} note${notes.length === 1 ? '' : 's'} · only you can see them`}</span>
        <Button size="sm" className="h-7" onClick={takeNote} data-testid="take-note"><NotePencil className="w-3.5 h-3.5" /> {isVideo ? 'Take note' : 'New note'}</Button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {composer && (
          <NoteComposer timestamp={composer.timestamp} isVideo={isVideo} dark={dark} saving={saving}
            onTimestampClear={() => setComposer({ timestamp: null })} onSave={save} onCancel={() => setComposer(null)} />
        )}
        {loading && notes.length === 0 ? <p className={`text-[13px] ${muted}`}>Loading notes…</p> : null}
        {!loading && notes.length === 0 && !composer && (
          <div className={`py-8 text-center text-[13px] ${muted}`} data-testid="notes-empty">
            <NotePencil className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {isVideo ? <p>Press <b>Take note</b> while the video plays — it pauses, remembers the time, and your note will jump back to that moment.</p> : <p>Keep your own notes for this lesson here.</p>}
          </div>
        )}
        {notes.map((n) => (
          <div key={n.id} className={`group rounded-md border p-2.5 ${dark ? 'border-white/10 bg-white/5' : 'border-border bg-card'}`} data-testid={`note-${n.id}`}>
            {editing === n.id ? (
              <NoteComposer initial={n.text} timestamp={n.timestamp_seconds} isVideo={false} dark={dark} saving={saving} onSave={(t) => update(n.id, t)} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <TimeChip seconds={n.timestamp_seconds} onSeek={onSeek} dark={dark} testid={`note-time-${n.id}`} />
                  <span className={`text-[11px] ${muted}`}>{timeAgo(n.created_at)}</span>
                  <span className="ml-auto flex opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" className={`p-1 rounded ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`} title="Edit" onClick={() => { setComposer(null); setEditing(n.id); }}><PencilSimple className="w-3.5 h-3.5" /></button>
                    <button type="button" className={`p-1 rounded ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`} title="Delete" onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`}><Trash className="w-3.5 h-3.5 text-destructive" /></button>
                  </span>
                </div>
                <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{n.text}</p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------- Community

function Avatar({ name, role, dark }) {
  const tutor = isTutorRole(role);
  return (
    <span className={`w-7 h-7 rounded-full flex-shrink-0 inline-flex items-center justify-center text-[11px] font-semibold ${tutor ? 'bg-amber-500 text-white' : dark ? 'bg-white/15 text-white' : 'bg-shell-primary/10 text-shell-primary'}`} title={name}>
      {initials(name)}
    </span>
  );
}

function Author({ name, role, when, dark }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0 text-[12px]">
      <span className="font-medium truncate">{name}</span>
      {isTutorRole(role) && <span className="inline-flex items-center gap-0.5 rounded px-1 h-4 text-[10px] font-semibold uppercase bg-amber-100 text-amber-800"><ChalkboardTeacher className="w-3 h-3" />Tutor</span>}
      <span className={dark ? 'text-white/40' : 'text-muted-foreground'}>· {timeAgo(when)}</span>
    </div>
  );
}

function PostComposer({ api, courseId, node, playerRef, isVideo, dark, readOnly, onPosted }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('question');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [timestamp, setTimestamp] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = async (k) => {
    setKind(k); setOpen(true);
    if (isVideo && playerRef?.current?.canSeek) { try { setTimestamp(Math.floor(await playerRef.current.getCurrentTime())); } catch { setTimestamp(null); } }
  };
  const reset = () => { setOpen(false); setTitle(''); setBody(''); setTimestamp(null); };
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    if (readOnly) { toast.info('Preview only — posts are not published'); reset(); return; }
    setSaving(true);
    try {
      await api.post(`/courses/${courseId}/nodes/${node.id}/posts`, { kind, title: title.trim() || null, body: body.trim(), timestamp_seconds: timestamp });
      reset(); onPosted?.(); toast.success(kind === 'question' ? 'Question posted' : 'Posted to the community');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not post'); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7" onClick={() => start('question')} data-testid="ask-question"><Question className="w-3.5 h-3.5" /> Ask a question</Button>
        <Button size="sm" variant="outline" className={`h-7 ${dark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} onClick={() => start('post')} data-testid="new-post"><Megaphone className="w-3.5 h-3.5" /> New post</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className={`rounded-md border p-2 space-y-2 ${dark ? 'border-white/15 bg-white/5' : 'border-border bg-card'}`} data-testid="post-composer">
      <div className="flex items-center gap-1 text-[12px]">
        {['question', 'post'].map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={`rounded px-2 h-6 ${kind === k ? 'bg-shell-primary text-white' : dark ? 'bg-white/10 text-white/70' : 'bg-muted text-muted-foreground'}`}>{k === 'question' ? 'Question' : 'Post'}</button>
        ))}
        {isVideo && (
          <label className={`ml-auto inline-flex items-center gap-1 text-[11px] ${dark ? 'text-white/60' : 'text-muted-foreground'}`}>
            <input type="checkbox" checked={timestamp !== null} onChange={async (e) => setTimestamp(e.target.checked ? Math.floor(await (playerRef?.current?.getCurrentTime?.() ?? 0)) : null)} />
            {timestamp !== null ? `at ${formatTime(timestamp)}` : 'attach video time'}
          </label>
        )}
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === 'question' ? 'Question title (optional)' : 'Title (optional)'} className={`text-[13px] ${dark ? 'bg-black/30 border-white/15 text-white' : ''}`} data-testid="post-title" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} autoFocus placeholder={kind === 'question' ? 'What would you like to ask?' : 'Share something with everyone taking this lesson…'} className={`text-[13px] ${dark ? 'bg-black/30 border-white/15 text-white' : ''}`} data-testid="post-body" />
      <div className="flex justify-end gap-1.5">
        <Button type="button" variant="ghost" size="sm" className="h-7" onClick={reset}>Cancel</Button>
        <Button type="submit" size="sm" className="h-7" disabled={saving || !body.trim()} data-testid="post-submit">{kind === 'question' ? 'Post question' : 'Post'}</Button>
      </div>
    </form>
  );
}

function ReplyBox({ onSubmit, onCancel, dark, saving }) {
  const [body, setBody] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) onSubmit(body.trim()); }} className="flex gap-1.5 items-start" data-testid="reply-box">
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} autoFocus placeholder="Write a reply…" className={`text-[13px] flex-1 ${dark ? 'bg-black/30 border-white/15 text-white' : ''}`}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); if (body.trim()) onSubmit(body.trim()); } if (e.key === 'Escape') onCancel(); }} data-testid="reply-text" />
      <div className="flex flex-col gap-1">
        <Button type="submit" size="sm" className="h-7" disabled={saving || !body.trim()} data-testid="reply-submit">Reply</Button>
        <Button type="button" variant="ghost" size="sm" className="h-7" onClick={onCancel}><X className="w-3.5 h-3.5" /></Button>
      </div>
    </form>
  );
}

function PostCard({ post, api, user, dark, onSeek, onChanged }) {
  const [replying, setReplying] = useState(false);
  const [expanded, setExpanded] = useState(post.replies.length <= 2);
  const [saving, setSaving] = useState(false);
  const mine = post.user_id === user?.id;
  const staff = isTutorRole(user?.role) && !user?._preview;
  const readOnly = !!user?._preview;
  const muted = dark ? 'text-white/50' : 'text-muted-foreground';

  const reply = async (body) => {
    if (readOnly) { toast.info('Preview only — replies are not published'); setReplying(false); return; }
    setSaving(true);
    try { await api.post(`/posts/${post.id}/replies`, { body }); setReplying(false); setExpanded(true); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not reply'); }
    finally { setSaving(false); }
  };
  const resolve = async () => { try { await api.post(`/posts/${post.id}/resolve`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } };
  const removePost = async () => { if (!window.confirm('Delete this post and its replies?')) return; try { await api.delete(`/posts/${post.id}`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); } };
  const removeReply = async (id) => { try { await api.delete(`/replies/${id}`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); } };

  const shown = expanded ? post.replies : post.replies.slice(0, 1);
  return (
    <article className={`rounded-md border p-2.5 space-y-2 ${dark ? 'border-white/10 bg-white/5' : 'border-border bg-card'}`} data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-2">
        <Avatar name={post.user_name} role={post.user_role} dark={dark} />
        <div className="min-w-0 flex-1">
          <Author name={post.user_name} role={post.user_role} when={post.created_at} dark={dark} />
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className={`inline-flex items-center gap-0.5 rounded px-1 h-4 text-[10px] font-semibold uppercase ${post.kind === 'question' ? (dark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-50 text-violet-700') : (dark ? 'bg-white/10 text-white/70' : 'bg-muted text-muted-foreground')}`}>
              {post.kind === 'question' ? <Question className="w-3 h-3" /> : <Megaphone className="w-3 h-3" />}{post.kind}
            </span>
            {post.kind === 'question' && post.resolved && <span className="inline-flex items-center gap-0.5 rounded px-1 h-4 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3" weight="fill" />Answered</span>}
            <TimeChip seconds={post.timestamp_seconds} onSeek={onSeek} dark={dark} />
          </div>
        </div>
        {(mine || staff) && !readOnly && (
          <button type="button" className={`p-1 rounded ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`} title="Delete post" onClick={removePost} data-testid={`post-delete-${post.id}`}><Trash className="w-3.5 h-3.5 text-destructive" /></button>
        )}
      </div>
      {post.title && <h4 className="text-[13px] font-semibold leading-snug">{post.title}</h4>}
      <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{post.body}</p>

      {post.replies.length > 0 && (
        <div className={`ml-3 pl-3 border-l space-y-2 ${dark ? 'border-white/10' : 'border-border'}`} data-testid={`replies-${post.id}`}>
          {shown.map((r) => (
            <div key={r.id} className="flex items-start gap-2 group">
              <Avatar name={r.user_name} role={r.user_role} dark={dark} />
              <div className="min-w-0 flex-1">
                <Author name={r.user_name} role={r.user_role} when={r.created_at} dark={dark} />
                <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{r.body}</p>
              </div>
              {(r.user_id === user?.id || staff) && !readOnly && (
                <button type="button" className={`p-1 rounded opacity-0 group-hover:opacity-100 ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`} title="Delete reply" onClick={() => removeReply(r.id)}><Trash className="w-3.5 h-3.5 text-destructive" /></button>
              )}
            </div>
          ))}
          {!expanded && post.replies.length > 1 && (
            <button type="button" className={`text-[12px] underline ${muted}`} onClick={() => setExpanded(true)} data-testid={`show-replies-${post.id}`}>Show all {post.replies.length} replies</button>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 text-[12px]">
        {replying ? null : <button type="button" className={`inline-flex items-center gap-1 hover:underline ${muted}`} onClick={() => setReplying(true)} data-testid={`reply-${post.id}`}><ArrowBendUpLeft className="w-3.5 h-3.5" /> Reply{post.reply_count ? ` (${post.reply_count})` : ''}</button>}
        {post.kind === 'question' && (mine || staff) && !readOnly && (
          <button type="button" className={`inline-flex items-center gap-1 hover:underline ${post.resolved ? 'text-emerald-600' : muted}`} onClick={resolve} data-testid={`resolve-${post.id}`}>
            <CheckCircle className="w-3.5 h-3.5" /> {post.resolved ? 'Answered' : 'Mark answered'}
          </button>
        )}
      </div>
      {replying && <ReplyBox onSubmit={reply} onCancel={() => setReplying(false)} dark={dark} saving={saving} />}
    </article>
  );
}

function CommunityTab({ api, courseId, node, playerRef, isVideo, user, dark, readOnly, onSeek, onCount }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | question | post | open
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/courses/${courseId}/nodes/${node.id}/posts`); setPosts(r.data || []); onCount?.((r.data || []).length); }
    catch { setPosts([]); }
    finally { setLoading(false); }
  }, [api, courseId, node.id, onCount]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => posts.filter((p) => filter === 'all' || (filter === 'open' ? p.kind === 'question' && !p.resolved : p.kind === filter)), [posts, filter]);
  const muted = dark ? 'text-white/50' : 'text-muted-foreground';
  const openQ = posts.filter((p) => p.kind === 'question' && !p.resolved).length;

  return (
    <div className="flex flex-col h-full">
      <div className={`px-3 py-2 border-b space-y-2 ${dark ? 'border-white/10' : 'border-border'}`}>
        <PostComposer api={api} courseId={courseId} node={node} playerRef={playerRef} isVideo={isVideo} dark={dark} readOnly={readOnly} onPosted={load} />
        <div className="flex items-center gap-1 text-[11px]">
          {[['all', `All (${posts.length})`], ['question', 'Questions'], ['open', `Unanswered${openQ ? ` (${openQ})` : ''}`], ['post', 'Posts']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded px-1.5 h-5 ${filter === k ? (dark ? 'bg-white/20 text-white' : 'bg-shell-primary/10 text-shell-primary font-medium') : muted}`} data-testid={`community-filter-${k}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="community-list">
        {loading && posts.length === 0 && <p className={`text-[13px] ${muted}`}>Loading…</p>}
        {!loading && visible.length === 0 && (
          <div className={`py-8 text-center text-[13px] ${muted}`} data-testid="community-empty">
            <ChatsCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>{posts.length === 0 ? 'No questions or posts yet — be the first to start the conversation.' : 'Nothing matches this filter.'}</p>
          </div>
        )}
        {visible.map((p) => <PostCard key={p.id} post={p} api={api} user={user} dark={dark} onSeek={onSeek} onChanged={load} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Panel

const TABS = [
  { key: 'overview', label: 'Overview', icon: Info },
  { key: 'notes', label: 'Notes', icon: NotePencil },
  { key: 'community', label: 'Community', icon: ChatsCircle },
  { key: 'results', label: 'Results', icon: Target },
];

const LessonPanel = forwardRef(function LessonPanel({ api, courseId, node, playerRef, user, theme = 'light', readOnly = false, defaultTab, className = '', onCollapse, onCounts, results }, ref) {
  const dark = theme === 'dark';
  const isVideo = node?.content_type === 'video';
  const [tab, setTab] = useState(defaultTab || (node?.overview ? 'overview' : 'notes'));
  const [counts, setCounts] = useState({});
  const notesRef = useRef(null);
  const rootRef = useRef(null);
  useEffect(() => { setTab(defaultTab || (node?.overview ? 'overview' : 'notes')); setCounts({}); }, [node?.id, defaultTab, node?.overview]);

  const onSeek = useCallback((seconds) => {
    const p = playerRef?.current;
    if (!p?.canSeek) { toast.info('This video source cannot be controlled from here'); return; }
    p.seekTo(seconds); p.play();
  }, [playerRef]);

  useImperativeHandle(ref, () => ({
    takeNote() {
      setTab('notes');
      // NotesTab stays mounted, but give React a tick in case the panel itself just opened
      let tries = 0;
      const run = () => { if (notesRef.current?.takeNote) notesRef.current.takeNote(); else if (tries++ < 20) setTimeout(run, 50); };
      setTimeout(run, 0);
    },
    scrollIntoView() { if (window.innerWidth < 1024) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); },
    showTab: setTab,
  }), []);

  const resultCount = results?.count || 0;
  useEffect(() => {
    setCounts((c) => (c.results === resultCount ? c : { ...c, results: resultCount || undefined }));
  }, [resultCount]);

  const setNotesCount = useCallback((n) => setCounts((c) => (c.notes === n ? c : { ...c, notes: n })), []);
  const setPostsCount = useCallback((n) => setCounts((c) => (c.community === n ? c : { ...c, community: n })), []);
  useEffect(() => { onCounts?.(counts); }, [counts, onCounts]);

  if (!node) return null;
  return (
    <div ref={rootRef} className={`flex flex-col h-full min-h-0 min-w-0 break-words ${dark ? 'bg-black/40 text-white' : 'bg-background'} ${className}`} data-testid="lesson-panel">
      <div className={`flex-shrink-0 flex items-stretch border-b ${dark ? 'border-white/10' : 'border-border'}`} role="tablist">
        {TABS.map(({ key, label, icon: TabIcon }) => {
          const active = tab === key;
          const badge = counts[key];
          return (
            <button key={key} type="button" role="tab" aria-selected={active} onClick={() => setTab(key)} data-testid={`lesson-tab-${key}`}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 h-9 text-[12px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? 'border-shell-primary text-shell-primary' : `border-transparent ${dark ? 'text-white/60 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`}`}>
              <TabIcon className="w-4 h-4" />{label}{badge ? <span className={`rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center text-[10px] ${active ? 'bg-shell-primary text-white' : dark ? 'bg-white/15' : 'bg-muted'}`}>{badge}</span> : null}
            </button>
          );
        })}
        {onCollapse && (
          <button type="button" onClick={onCollapse} title="Collapse this panel" data-testid="collapse-lesson-panel"
            className={`px-2 flex items-center border-b-2 border-transparent -mb-px ${dark ? 'text-white/50 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`}>
            <CaretDoubleRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === 'overview' && <div className="h-full overflow-y-auto"><OverviewTab node={node} dark={dark} user={user} courseId={courseId} /></div>}
        <div className={`h-full ${tab === 'notes' ? '' : 'hidden'}`}>
          <NotesTab key={node.id} ref={notesRef} api={api} courseId={courseId} node={node} playerRef={playerRef} isVideo={isVideo} dark={dark} readOnly={readOnly} onSeek={onSeek} onCount={setNotesCount} />
        </div>
        {tab === 'results' && (
          <div className="h-full overflow-y-auto">
            <NodeResultsTab latest={results?.latest} history={results?.history} loading={results?.loading} dark={dark} />
          </div>
        )}
        <div className={`h-full ${tab === 'community' ? '' : 'hidden'}`}>
          <CommunityTab key={node.id} api={api} courseId={courseId} node={node} playerRef={playerRef} isVideo={isVideo} user={user} dark={dark} readOnly={readOnly} onSeek={onSeek} onCount={setPostsCount} />
        </div>
      </div>
    </div>
  );
});

export default LessonPanel;
